/**
 * missingAlt.ts — shared accessibility helpers for image alt-text checking.
 *
 * Lifted from components/editor-chrome/rail/cards/ImageMetadataCard.tsx so the
 * pre-publish gate can count missing-alt issues alongside contract/provenance
 * violations without depending on the card component. The card keeps its own
 * copy for rendering; this is the canonical util for the gate.
 */
import type { MetadataViolation } from './metadataCriteria';

/** Minimum alt-text length before we flag it as "missing"/too short. */
export const MIN_ALT_LENGTH = 6;

/**
 * Minimal image shape the alt checker needs. Structurally compatible with
 * EditorImageMeta (ImageMetadataCard.tsx) so callers can pass either type.
 */
export interface GateImage {
  /** Stable identifier for the image within the article. */
  id: string;
  /** Current alt text. */
  alt: string;
  /**
   * When true, an empty alt is treated as intentional (a decorative image
   * where alt="" is the correct screen-reader behavior).
   */
  decorative?: boolean;
}

/** True when the alt text is empty or shorter than MIN_ALT_LENGTH after trimming. */
export function isAltWeak(alt: string): boolean {
  return (alt ?? '').trim().length < MIN_ALT_LENGTH;
}

/** True when the user has marked this image decorative AND kept the alt empty. */
export function isEffectivelyDecorative(image: GateImage): boolean {
  return image.decorative === true && (image.alt ?? '').trim() === '';
}

/**
 * Build accessibility violations for the article's images. Each image with
 * weak alt text that is NOT intentionally decorative produces one warn-tier
 * violation (field 'images', category 'accessibility').
 *
 * All image violations share field 'images' so the badge's per-field dedup
 * counts the images axis as ONE field needing attention (consistent with how
 * status/human_qa/human_verify pseudo-fields each count once). The dialog
 * still renders each violation row individually.
 */
export function missingAltViolations(images: readonly GateImage[]): MetadataViolation[] {
  const out: MetadataViolation[] = [];
  for (const image of images) {
    if (isAltWeak(image.alt) && !isEffectivelyDecorative(image)) {
      out.push({
        ruleId: 'image_missing_alt',
        field: 'images',
        tier: 'warn',
        severity: 'warn',
        category: 'accessibility',
        message: `Image "${image.id}" is missing alt text or it is too short (under ${MIN_ALT_LENGTH} characters). Add a description or mark it decorative.`,
        sourceNote: 'accessibility (image alt)',
      });
    }
  }
  return out;
}
